import { Button, Card, Form, Input, Select, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Row = {
  id: string;
  case_no: string;
  referral_on: string;
  referral_reason: string;
  status: string;
  outcome_code?: string | null;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
};

export default function InterventionListPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.intervention.manage') || user?.permissions?.includes('education.admin')
  );
  const [status, setStatus] = useState<string | undefined>();
  const [q, setQ] = useState('');

  const list = useQuery({
    queryKey: ['education-intervention', status, q],
    queryFn: async () => {
      const res = await api.get<Row[]>('/education/intervention/cases', {
        params: { status, q: q || undefined },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  return (
    <EducationPageChrome
      title={t('intervention')}
      description={t('interventionIntro')}
      extra={
        canManage ? (
          <Button type="primary" onClick={() => navigate('/education/intervention/new')}>
            {t('newInterventionReferral')}
          </Button>
        ) : null
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Form layout="inline" onFinish={() => list.refetch()}>
            <Form.Item label={t('status')}>
              <Select
                allowClear
                style={{ minWidth: 160 }}
                value={status}
                onChange={(v) => setStatus(v || undefined)}
                options={[
                  { value: 'referred', label: t('statusReferred') },
                  { value: 'planning', label: t('statusPlanning') },
                  { value: 'active', label: t('statusActive') },
                  { value: 'pending_outcome', label: t('statusPendingOutcome') },
                  { value: 'closed', label: t('statusClosed') },
                  { value: 'cancelled', label: t('statusCancelled') },
                ]}
              />
            </Form.Item>
            <Form.Item label={t('search')}>
              <Input value={q} onChange={(e) => setQ(e.target.value)} allowClear style={{ width: 180 }} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={list.isFetching}>
              {t('filter')}
            </Button>
          </Form>
        </Card>
        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={list.isLoading}
            dataSource={list.data ?? []}
            pagination={{ pageSize: 25 }}
            onRow={(row) => ({
              onClick: () => navigate(`/education/intervention/${row.id}`),
              style: { cursor: 'pointer' },
            })}
            columns={[
              { title: t('caseNo'), dataIndex: 'case_no', width: 150 },
              { title: t('referralOn'), dataIndex: 'referral_on', width: 120 },
              {
                title: t('student'),
                render: (_: unknown, row: Row) =>
                  `${row.admission_no || ''} — ${row.first_name || ''} ${row.last_name || ''}`.trim(),
              },
              { title: t('referralReason'), dataIndex: 'referral_reason', ellipsis: true },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 140,
                render: (v: string) => <Tag>{v}</Tag>,
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
